import './settings/settings-leaf.css';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button } from './ui.tsx';
import { ConnectProviderLink, oauthErrorMessage, ProviderMark } from './social-buttons.tsx';
import { isOAuthProvider, OAUTH_PROVIDER_LABELS, type OAuthProvider } from '@shared/constants/index.ts';

/**
 * Settings → Connected accounts.
 *
 * Every sign-in method a user holds is interchangeable, so this is a list of
 * them rather than a switch between them: a password and any of the three
 * providers can all be attached at once, and any one of them signs in.
 *
 * The one rule the UI has to make visible is that you cannot remove your last
 * way in. The server refuses it regardless (POST would fail), but explaining it
 * before the click is better than after.
 */
export default function ConnectedAccountsSection({
  returnTo = '/settings',
  passwordTo,
}: {
  returnTo?: string;
  /**
   * Where "Set a password" goes when the password form is on another page.
   * Unset, it jumps to the form on this one (/settings, /crew-account).
   */
  passwordTo?: string;
}) {
  const { t } = useTranslation();
  const { user, refresh } = useSession();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const identities = useQuery({
    queryKey: ['auth-identities'],
    queryFn: () => authApi.identities(),
  });

  const disconnect = useMutation({
    mutationFn: (provider: OAuthProvider) => authApi.disconnectIdentity(provider),
    onSuccess: async (_result, provider) => {
      setNotice({
        tone: 'success',
        text: t('connectedAccounts.disconnectSuccess', { provider: OAUTH_PROVIDER_LABELS[provider] }),
      });
      await queryClient.invalidateQueries({ queryKey: ['auth-identities'] });
    },
    onError: (error) => {
      setNotice({
        tone: 'error',
        text: error instanceof ApiRequestError ? error.message : t('connectedAccounts.disconnectError'),
      });
    },
  });

  // The connect flow is a full-page redirect, so its outcome arrives as a query
  // parameter rather than a mutation result. Clear it once read, so a reload does
  // not replay a stale banner.
  //
  // Validated rather than cast: indexing the label map with an arbitrary query
  // value resolves inherited keys too, so `?oauth_linked=__proto__` would yield
  // an object React refuses to render and take the whole page down with it.
  const linkedParam = searchParams.get('oauth_linked');
  const linked = linkedParam && isOAuthProvider(linkedParam) ? linkedParam : null;
  const linkError = oauthErrorMessage(searchParams.get('oauth_error'), searchParams.get('oauth_provider'));

  function dismissRedirectNotice() {
    const next = new URLSearchParams(searchParams);
    next.delete('oauth_linked');
    next.delete('oauth_error');
    next.delete('oauth_provider');
    setSearchParams(next, { replace: true });
    // The session's `hasPassword` is unchanged by a link, but the identity list
    // is not — and the freshly linked provider should appear without a reload.
    void queryClient.invalidateQueries({ queryKey: ['auth-identities'] });
    void refresh();
  }

  const connected = identities.data?.items ?? [];
  const available = identities.data?.available ?? [];
  const hasPassword = user?.hasPassword ?? true;
  const connectable = available.filter((provider) => !connected.some((item) => item.provider === provider));

  // Nothing to show if the deployment offers no providers and none is connected
  // — which is every deployment that has not set up social sign-in.
  if (available.length === 0 && connected.length === 0) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="connected-heading">
      <div className="work-section-heading">
        <h2 id="connected-heading" className="mb-2 text-lg font-semibold">
          {t('connectedAccounts.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('connectedAccounts.description')}</p>

      {linked ? (
        <div className="mb-4">
          <Alert tone="success">
            {t('connectedAccounts.linkedSuccess', { provider: OAUTH_PROVIDER_LABELS[linked] })}{' '}
            <button type="button" className="underline" onClick={dismissRedirectNotice}>
              {t('connectedAccounts.dismiss')}
            </button>
          </Alert>
        </div>
      ) : null}

      {linkError ? (
        <div className="mb-4">
          <Alert tone="error">
            {linkError}{' '}
            <button type="button" className="underline" onClick={dismissRedirectNotice}>
              {t('connectedAccounts.dismiss')}
            </button>
          </Alert>
        </div>
      ) : null}

      {notice ? (
        <div className="mb-4">
          <Alert tone={notice.tone}>{notice.text}</Alert>
        </div>
      ) : null}

      <ul className="mb-4 flex flex-col gap-3">
        {/* The password sits in the same list as the providers, because it is one
            sign-in method among several rather than the "real" one. */}
        <li className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--color-line)] pb-3">
          <div>
            <p className="font-medium">{t('connectedAccounts.passwordLabel')}</p>
            <p className="text-sm text-[color:var(--color-muted)]">
              {hasPassword ? t('connectedAccounts.passwordSet') : t('connectedAccounts.passwordNotSet')}
            </p>
          </div>
          {hasPassword ? null : passwordTo ? (
            <Link className="btn btn-secondary" to={passwordTo}>
              {t('connectedAccounts.setPasswordLink')}
            </Link>
          ) : (
            <a className="btn btn-secondary" href="#password-heading">
              {t('connectedAccounts.setPasswordLink')}
            </a>
          )}
        </li>

        {connected.map((identity) => {
          // Removing this would leave no way in at all: no password, and it is
          // the only provider.
          const isLastWayIn = !hasPassword && connected.length === 1;

          return (
            <li
              key={identity.provider}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--color-line)] pb-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <ProviderMark provider={identity.provider} />
                <div className="min-w-0">
                  <p className="font-medium">{OAUTH_PROVIDER_LABELS[identity.provider]}</p>
                  <p className="truncate text-sm text-[color:var(--color-muted)]">
                    {identity.email ?? identity.displayName ?? t('connectedAccounts.connectedFallback')}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <Button
                  variant="ghost"
                  disabled={isLastWayIn}
                  busy={disconnect.isPending && disconnect.variables === identity.provider}
                  onClick={() => disconnect.mutate(identity.provider)}
                >
                  {t('connectedAccounts.disconnect')}
                </Button>
                {isLastWayIn ? (
                  <p className="mt-1 max-w-xs text-xs text-[color:var(--color-muted)]">
                    {t('connectedAccounts.lastWayInWarning')}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {connectable.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {connectable.map((provider) => (
            <ConnectProviderLink key={provider} provider={provider} returnTo={returnTo} />
          ))}
        </div>
      ) : null}
    </section>
  );
}
